import React from 'react';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import ForumOutlinedIcon from '@mui/icons-material/ForumOutlined';
import HubOutlinedIcon from '@mui/icons-material/HubOutlined';
import PsychologyAltOutlinedIcon from '@mui/icons-material/PsychologyAltOutlined';
import RuleOutlinedIcon from '@mui/icons-material/RuleOutlined';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import GavelOutlinedIcon from '@mui/icons-material/GavelOutlined';
import LibraryBooksOutlinedIcon from '@mui/icons-material/LibraryBooksOutlined';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import PolicyOutlinedIcon from '@mui/icons-material/PolicyOutlined';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import AddLinkOutlinedIcon from '@mui/icons-material/AddLinkOutlined';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import {Avatar, Button, Chip, Divider, Stack, Typography} from '@mui/material';

/**
 * Context-aware empty state content.
 *
 * Keys map to emptyStatePreset values:
 *   global      — no context selected, system-wide Q&A
 *   analytic    — FINTEL analytic products (productType.value = "analytic")
 *   disclosure  — Disclosure reports
 *   report      — Drafts and unclassified products
 *   chart       — Whiteboard / chart AI hub (GlobalAiAssistantPanel when chart route active)
 *   sourceFile  — A specific source file
 */
const EMPTY_STATE_CONTENT = Object.freeze({

    // ─── Global (no report context) ──────────────────────────────────────────
    global: {
        intro: 'Select a report context above to unlock specialized writing and analysis workflows — or ask anything across the system right now.',
        globalActions: [
            {
                label: 'What is in the system?',
                icon: ForumOutlinedIcon,
                action: {type: 'prompt', prompt: 'Give me a summary of the most recent products and key topics in the system.'},
            },
            {
                label: 'Find relevant sources',
                icon: SearchOutlinedIcon,
                action: {type: 'prompt', prompt: 'Find the most relevant sources for the topics I am currently working on.'},
            },
            {
                label: 'Find contradictions',
                icon: PsychologyAltOutlinedIcon,
                action: {type: 'prompt', prompt: 'Identify contradictions or conflicting assessments across the latest products in the system.'},
            },
            {
                label: 'Surface knowledge gaps',
                icon: AutoAwesomeOutlinedIcon,
                action: {type: 'prompt', prompt: 'What are the most significant knowledge gaps across current active products?'},
            },
        ],
        contextPreviews: [
            {
                type: 'report',
                label: 'Draft Report',
                icon: AssignmentOutlinedIcon,
                chipColor: 'default',
                description: 'Writing and analysis assistance for in-progress reports.',
                workflows: ['Writing Assistance', 'Analytical Reasoning', 'Source Discovery'],
            },
            {
                type: 'analytic',
                label: 'FINTEL',
                icon: ArticleOutlinedIcon,
                chipColor: 'primary',
                description: 'Deep tradecraft review and analytical stress-testing.',
                workflows: ['Question Answering', 'Opinions'],
            },
            {
                type: 'disclosure',
                label: 'Disclosure',
                icon: PolicyOutlinedIcon,
                chipColor: 'warning',
                description: 'Compliance validation and source traceability checks.',
                workflows: ['Compliance Review', 'Source Traceability', 'Narrative Coherence'],
            },
            {
                type: 'chart',
                label: 'Chart',
                icon: HubOutlinedIcon,
                chipColor: 'primary',
                description: 'Ask questions about the chart, find related people and organizations, and review suggested additions before they are applied.',
                workflows: ['Ask questions', 'Find related context', 'Review additions'],
            },
        ],
        automaticCapabilities: [
            {title: 'Question Answering', description: 'Ask questions about uploaded sources, referenced evidence, or the current product at any time.', icon: ForumOutlinedIcon},
            {title: 'Evidence & Citation Integrity', description: 'Claim-to-evidence mapping, supporting vs contradicting evidence checks, and source traceability validation are applied automatically during analysis runs.', icon: FactCheckOutlinedIcon},
            {title: 'Entity & Knowledge Structuring', description: 'Entity linking and knowledge-graph enrichment are applied automatically when the document context supports it.', icon: HubOutlinedIcon},
        ],
    },

    // ─── FINTEL / Analytic products ──────────────────────────────────────────
    analytic: {
        intro: 'Start with a guided analytic workflow below, or ask direct questions about this FINTEL product.',
        manualWorkflows: [
            {
                title: 'Tradecraft Enforcement',
                description: 'Run structured checks against ICD-203 standards, claim support, and reporting rigor.',
                actions: [
                    {
                        label: 'Apply ICD-203',
                        icon: RuleOutlinedIcon,
                        action: {type: 'prompt', prompt: 'Apply ICD-203 standards to this document and provide a compliance checklist with revisions.'},
                    },
                    {
                        label: 'Validate Claims & Citations',
                        icon: FactCheckOutlinedIcon,
                        action: {type: 'prompt', prompt: 'Validate claims and citations in this document and list the highest-priority corrections.'},
                    },
                ],
            },
            {
                title: 'Analytical Reasoning',
                description: 'Stress-test the assessment with competing interpretations and gap analysis.',
                actions: [
                    {
                        label: 'Generate Alternatives',
                        icon: PsychologyAltOutlinedIcon,
                        action: {type: 'prompt', prompt: 'Generate alternative hypotheses for the main assessment in this document.'},
                    },
                    {
                        label: 'Find Contradictions',
                        icon: AutoAwesomeOutlinedIcon,
                        action: {type: 'prompt', prompt: 'Identify contradictions and analytical gaps in this document and propose concrete fixes.'},
                    },
                ],
            },
            {
                title: 'Contextual Reading List',
                description: 'Create suggested queries for a focused source search based on the analytical context.',
                actions: [
                    {
                        label: 'Based on requirements',
                        icon: AutoAwesomeOutlinedIcon,
                        action: {type: 'prompt', prompt: 'Create a contextual reading list based on the requirements for this product.'},
                    },
                    {
                        label: 'Based on product gaps',
                        icon: SearchOutlinedIcon,
                        action: {type: 'prompt', prompt: 'Create a contextual reading list focused on the highest-priority gaps in my current product.'},
                    },
                ],
            },
        ],
        automaticCapabilities: [
            {title: 'Question Answering', description: 'Ask questions about this FINTEL product, its sources, or referenced evidence at any time.', icon: ForumOutlinedIcon},
            {title: 'Evidence & Citation Integrity', description: 'Claim-to-evidence mapping, supporting vs contradicting evidence checks, and source traceability.', icon: FactCheckOutlinedIcon},
            {title: 'Entity & Knowledge Structuring', description: 'Entity linking and knowledge-graph enrichment when the document context supports it.', icon: HubOutlinedIcon},
        ],
    },

    // ─── Disclosures ─────────────────────────────────────────────────────────
    disclosure: {
        intro: 'Start with a guided compliance workflow, or ask direct questions about this disclosure.',
        manualWorkflows: [
            {
                title: 'Compliance Review',
                description: 'Validate the disclosure against applicable regulatory and tradecraft standards.',
                actions: [
                    {
                        label: 'Check compliance',
                        icon: GavelOutlinedIcon,
                        action: {type: 'prompt', prompt: 'Review this disclosure for compliance issues and list the highest-priority corrections.'},
                    },
                    {
                        label: 'Validate redactions',
                        icon: RuleOutlinedIcon,
                        action: {type: 'prompt', prompt: 'Identify any content that may require additional redaction or classification review in this disclosure.'},
                    },
                ],
            },
            {
                title: 'Source Traceability',
                description: 'Map claims back to source documents and identify unsupported assertions.',
                actions: [
                    {
                        label: 'Trace all claims',
                        icon: FactCheckOutlinedIcon,
                        action: {type: 'prompt', prompt: 'Map all claims in this disclosure to their supporting source documents and flag any unsupported assertions.'},
                    },
                    {
                        label: 'Find citation gaps',
                        icon: SearchOutlinedIcon,
                        action: {type: 'prompt', prompt: 'Identify statements in this disclosure that lack adequate source citation.'},
                    },
                ],
            },
            {
                title: 'Narrative Coherence',
                description: 'Check for internal consistency, contradictions, and logical flow.',
                actions: [
                    {
                        label: 'Check consistency',
                        icon: PsychologyAltOutlinedIcon,
                        action: {type: 'prompt', prompt: 'Check this disclosure for internal contradictions, inconsistencies, or logical gaps.'},
                    },
                    {
                        label: 'Summarize for review',
                        icon: LibraryBooksOutlinedIcon,
                        action: {type: 'prompt', prompt: 'Generate a structured summary of this disclosure for reviewer reference.'},
                    },
                ],
            },
        ],
        automaticCapabilities: [
            {title: 'Compliance Checking', description: 'Regulatory and classification compliance checks applied during analysis runs.', icon: GavelOutlinedIcon},
            {title: 'Source Traceability', description: 'Claim-to-source mapping and citation integrity validation.', icon: FactCheckOutlinedIcon},
            {title: 'Entity Linking', description: 'Automatic entity recognition and cross-document relationship surfacing.', icon: HubOutlinedIcon},
        ],
    },

    // ─── Drafts / generic reports ────────────────────────────────────────────
    report: {
        intro: 'Start with a guided analysis below, or ask direct questions about uploaded sources and products.',
        manualWorkflows: [
            {
                title: 'Contextual Reading List',
                description: 'Create suggested queries for a focused source search based on the current analytical context and evidence review.',
                actions: [
                    {
                        label: 'Based on requirements',
                        icon: AutoAwesomeOutlinedIcon,
                        action: {type: 'prompt', prompt: 'Create a contextual reading list based on the requirements for this product.'},
                    },
                    {
                        label: 'Based on the product gaps',
                        icon: PsychologyAltOutlinedIcon,
                        action: {type: 'prompt', prompt: 'Create a contextual reading list focused on the highest-priority gaps in my current product.'},
                    },
                ],
            },
            {
                title: 'Tradecraft Enforcement',
                description: 'Run structured checks against standards, claim support, and reporting rigor.',
                actions: [
                    {
                        label: 'Validate Claims & Citations',
                        icon: FactCheckOutlinedIcon,
                        action: {type: 'prompt', prompt: 'Validate claims and citations in this document and list the highest-priority corrections.'},
                    },
                    {
                        label: 'Apply ICD-203',
                        icon: RuleOutlinedIcon,
                        action: {type: 'prompt', prompt: 'Apply ICD-203 standards to this document and provide a compliance checklist with revisions.'},
                    },
                ],
            },
            {
                title: 'Analytical Reasoning',
                description: 'Stress-test the assessment with competing interpretations and gap analysis.',
                actions: [
                    {
                        label: 'Generate Alternatives',
                        icon: PsychologyAltOutlinedIcon,
                        action: {type: 'prompt', prompt: 'Generate alternative hypotheses for the main assessment in this document.'},
                    },
                    {
                        label: 'Find Contradictions',
                        icon: AutoAwesomeOutlinedIcon,
                        action: {type: 'prompt', prompt: 'Identify contradictions and analytical gaps in this document and propose concrete fixes.'},
                    },
                ],
            },
        ],
        automaticCapabilities: [
            {title: 'Question Answering', description: 'Ask questions about uploaded sources, referenced evidence, or the current product at any time.', icon: ForumOutlinedIcon},
            {title: 'Evidence & Citation Integrity', description: 'Claim-to-evidence mapping, supporting vs contradicting evidence checks, and source traceability validation are applied automatically during analysis runs.', icon: FactCheckOutlinedIcon},
            {title: 'Entity & Knowledge Structuring', description: 'Entity linking and knowledge-graph enrichment are applied automatically when the document context supports it.', icon: HubOutlinedIcon},
        ],
    },

    // ─── Chart / whiteboard ──────────────────────────────────────────────────
    chart: {
        intro: 'Ask questions about what is already drawn, find related people and organizations from the knowledge base, or review suggested additions before anything is added to the chart.',
        capabilitiesButtonLabel: 'What can I help with?',
        manualWorkflows: [
            {
                title: 'Chart overview',
                description: 'A concise briefing of who appears on this chart and how they are connected.',
                actions: [
                    {
                        label: 'Summarize this chart',
                        icon: HubOutlinedIcon,
                        action: {
                            type: 'prompt',
                            prompt: 'Summarize this chart: the question it addresses, the central actors, and the most important relationships.',
                        },
                    },
                    {
                        label: 'Trace indirect connections',
                        icon: AccountTreeOutlinedIcon,
                        action: {type: 'trace_indirect_connections'},
                    },
                ],
            },
            {
                title: 'Enrich from the knowledge base',
                description: 'Find related people, organizations, and connections that are not yet on the chart.',
                actions: [
                    {
                        label: 'Suggest missing connections',
                        icon: AutoAwesomeOutlinedIcon,
                        action: {
                            type: 'prompt',
                            prompt: 'Suggest related people, organizations, and connections that are missing from this chart. I will review and apply or reject them.',
                        },
                    },
                ],
            },
            {
                title: 'Suggested additions',
                description: 'Review proposed people, organizations, and connections before they are added. Nothing is drawn until you apply the change.',
                actions: [
                    {
                        label: 'Add & connect an entity',
                        icon: AddLinkOutlinedIcon,
                        action: {
                            type: 'prompt',
                            prompt: 'Propose adding a relevant entity from the knowledge base and connecting it to an existing entity already on this chart.',
                        },
                    },
                ],
            },
        ],
        automaticCapabilities: [
            {title: 'What is already on the chart', description: 'Answers start from the people, organizations, and connections drawn here.', icon: HubOutlinedIcon},
            {title: 'Knowledge base', description: 'Look up related profiles, connections, and organization members when you need more than what is on the chart.', icon: SearchOutlinedIcon},
            {title: 'You stay in control', description: 'Suggested additions and connections are never applied automatically. You choose to apply or reject each one.', icon: FactCheckOutlinedIcon},
        ],
    },

    // ─── Source file ─────────────────────────────────────────────────────────
    sourceFile: {
        intro: 'Start with source-focused actions below, or ask direct questions about this file.',
        manualWorkflows: [
            {
                title: 'Source Understanding',
                description: 'Get a fast, structured understanding of what this file says and why it matters.',
                actions: [
                    {
                        label: 'Summarize this file',
                        icon: AutoAwesomeOutlinedIcon,
                        action: {type: 'prompt', prompt: 'Summarize this source file with key findings, claims, and what is most relevant for the current report.'},
                    },
                    {
                        label: 'Extract key claims',
                        icon: FactCheckOutlinedIcon,
                        action: {type: 'prompt', prompt: 'Extract the key claims from this source file and rank them by analytical importance.'},
                    },
                ],
            },
            {
                title: 'Evidence Quality',
                description: 'Evaluate confidence, sourcing quality, and potential weaknesses in this document.',
                actions: [
                    {
                        label: 'Assess reliability',
                        icon: RuleOutlinedIcon,
                        action: {type: 'prompt', prompt: 'Assess the reliability of this source file and list credibility strengths, weaknesses, and caveats.'},
                    },
                    {
                        label: 'Find contradictions',
                        icon: PsychologyAltOutlinedIcon,
                        action: {type: 'prompt', prompt: 'Identify contradictions, unsupported assertions, and evidence gaps in this source file.'},
                    },
                ],
            },
            {
                title: 'Actionable Next Steps',
                description: 'Turn this source into concrete follow-up questions and investigative actions.',
                actions: [
                    {
                        label: 'Generate follow-ups',
                        icon: ForumOutlinedIcon,
                        action: {type: 'prompt', prompt: 'Generate high-value follow-up questions based on this source file.'},
                    },
                    {
                        label: 'Draft citation notes',
                        icon: HubOutlinedIcon,
                        action: {type: 'prompt', prompt: 'Draft citation-ready notes from this source file, including exact points worth referencing.'},
                    },
                ],
            },
        ],
        automaticCapabilities: [
            {title: 'Question Answering', description: 'Ask targeted questions about the currently opened source file at any time.', icon: ForumOutlinedIcon},
            {title: 'Claim and Evidence Review', description: 'Validate whether claims in this file are supported, contradicted, or underspecified.', icon: FactCheckOutlinedIcon},
            {title: 'Analytical Structuring', description: 'Turn this source into structured notes, entities, and usable report inputs.', icon: HubOutlinedIcon},
        ],
    },
});

/** Human-readable label and chip colour per context */
const CONTEXT_META = {
    global:     {label: 'Global',      chipColor: 'default'},
    analytic:   {label: 'FINTEL',      chipColor: 'primary'},
    disclosure: {label: 'Disclosure',  chipColor: 'warning'},
    report:     {label: 'Draft',       chipColor: 'default'},
    chart:      {label: 'Chart',       chipColor: 'primary'},
    sourceFile: {label: 'Source File', chipColor: 'default'},
};

/** Shared header bubble used by both global and contextual views */
const AssistantHeader = ({meta, isGlobal, isChart, documentTitle, introText}) => (
    <Stack direction="row" spacing={1} alignItems="flex-start">
        <Avatar sx={{width: 26, height: 26, bgcolor: 'primary.main', fontSize: 10, fontWeight: 700, flexShrink: 0}}>
            AI
        </Avatar>
        <Stack spacing={0.5}>
            <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
                <Typography variant="caption" fontWeight={600} sx={{fontSize: 12}}>
                    AI Assistant
                </Typography>
                <Chip
                    label={meta.label}
                    size="small"
                    color={meta.chipColor}
                    sx={{height: 16, fontSize: 10}}
                />
                <Chip label="Ready" size="small" sx={{height: 16, fontSize: 10}}/>
            </Stack>
            <Typography variant="caption" sx={{fontSize: 11}}>
                {isGlobal
                    ? 'I can help you work across the entire system, or go deep on any specific report or chart.'
                    : <>I can help you work through{' '}
                        {documentTitle
                            ? <strong>{documentTitle}</strong>
                            : (isChart ? 'this chart' : 'this document')}{' '}
                        in a few ways.</>
                }
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{fontSize: 11}}>
                {introText}
            </Typography>
        </Stack>
    </Stack>
);

/** Renders the global "discovery" empty state with context-type previews */
const GlobalEmptyState = ({content, onAction, promptActionDisabled}) => (
    <Stack spacing={1.5}>

        {/* Context-type preview cards */}
        <Stack spacing={0.5}>
            <Stack direction="row" spacing={0.5} alignItems="center" sx={{mb: 0.25}}>
                <ArrowUpwardIcon sx={{fontSize: 11, color: 'text.secondary'}}/>
                <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.04em'}}>
                    Select a context to unlock
                </Typography>
            </Stack>
            {content.contextPreviews.map((preview) => {
                const Icon = preview.icon;
                return (
                    <Stack
                        key={preview.type}
                        sx={{
                            p: 1,
                            borderRadius: 1,
                            bgcolor: 'grey.50',
                            border: '1px solid',
                            borderColor: 'divider',
                        }}
                        spacing={0.5}
                    >
                        <Stack direction="row" spacing={0.75} alignItems="center">
                            <Icon sx={{fontSize: 13, color: 'primary.main', flexShrink: 0}}/>
                            <Typography variant="caption" fontWeight={700} sx={{fontSize: 11}}>
                                {preview.label}
                            </Typography>
                            <Chip
                                label={preview.label}
                                size="small"
                                color={preview.chipColor}
                                sx={{height: 14, fontSize: 9, display: 'none'}}
                            />
                        </Stack>
                        <Typography variant="caption" color="text.secondary" sx={{fontSize: 10, lineHeight: 1.4}}>
                            {preview.description}
                        </Typography>
                        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                            {preview.workflows.map((name) => (
                                <Chip
                                    key={name}
                                    label={name}
                                    size="small"
                                    variant="outlined"
                                    sx={{height: 18, fontSize: 10, cursor: 'default', pointerEvents: 'none'}}
                                />
                            ))}
                        </Stack>
                    </Stack>
                );
            })}
        </Stack>

        <Divider sx={{my: 0.25}}/>

        {/* Global quick-start actions */}
        <Stack spacing={0.5}>
            <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.04em'}}>
                Ask without context
            </Typography>
            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                {content.globalActions.map((action) => {
                    const Icon = action.icon;
                    return (
                        <Button
                            key={action.label}
                            size="small"
                            variant="outlined"
                            startIcon={<Icon sx={{fontSize: '13px !important'}}/>}
                            onClick={() => onAction?.(action.action)}
                            disabled={action.action?.type === 'prompt' && promptActionDisabled}
                            sx={{fontSize: 11, py: 0.25, px: 0.75, minHeight: 0}}
                        >
                            {action.label}
                        </Button>
                    );
                })}
            </Stack>
        </Stack>

        <Stack direction="row">
            <Button
                size="small"
                variant="outlined"
                onClick={() => onAction?.({type: 'prompt', prompt: 'List all AI capabilities available'})}
                disabled={promptActionDisabled}
                sx={{fontSize: 11, py: 0.25}}
            >
                List all AI capabilities
            </Button>
        </Stack>

        {/* Always-available capabilities */}
        {content.automaticCapabilities?.length > 0 && (
            <Stack spacing={1.25} pt={0.25}>
                <Divider sx={{my: 0}}/>
                {content.automaticCapabilities.map((cap) => {
                    const Icon = cap.icon;
                    return (
                        <Stack key={cap.title} direction="row" spacing={1} alignItems="flex-start">
                            <Icon sx={{fontSize: 14, color: 'text.secondary', mt: 0.1, flexShrink: 0}}/>
                            <Stack spacing={0.1}>
                                <Typography variant="caption" fontWeight={600} sx={{fontSize: 11}}>
                                    {cap.title}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" sx={{fontSize: 10}}>
                                    {cap.description}
                                </Typography>
                            </Stack>
                        </Stack>
                    );
                })}
            </Stack>
        )}
    </Stack>
);

/** Renders the contextual (document-scoped) empty state with workflow cards */
const ContextualEmptyState = ({content, onAction, promptActionDisabled}) => (
    <Stack spacing={1.5}>
        {/* Workflow cards */}
        <Stack spacing={0.75}>
            {content.manualWorkflows.map((workflow) => (
                <Stack
                    key={workflow.title}
                    sx={{p: 1, borderRadius: 1, bgcolor: 'grey.50', border: '1px solid', borderColor: 'divider'}}
                >
                    <Stack spacing={0.75}>
                        <Stack spacing={0.25}>
                            <Typography variant="caption" fontWeight={700} sx={{fontSize: 11}}>
                                {workflow.title}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{fontSize: 10}}>
                                {workflow.description}
                            </Typography>
                        </Stack>
                        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                            {workflow.actions.map((action) => {
                                const Icon = action.icon;
                                return (
                                    <Button
                                        key={action.label}
                                        size="small"
                                        variant="outlined"
                                        startIcon={<Icon sx={{fontSize: '13px !important'}}/>}
                                        onClick={() => onAction?.(action.action)}
                                        disabled={
                                            (action.action?.type === 'prompt' && promptActionDisabled)
                                            || (action.action?.type === 'trace_indirect_connections' && promptActionDisabled)
                                        }
                                        sx={{fontSize: 11, py: 0.25, px: 0.75, minHeight: 0}}
                                    >
                                        {action.label}
                                    </Button>
                                );
                            })}
                        </Stack>
                    </Stack>
                </Stack>
            ))}
        </Stack>

        <Stack direction="row">
            <Button
                size="small"
                variant="outlined"
                onClick={() => onAction?.({type: 'prompt', prompt: 'List all AI capabilities available'})}
                disabled={promptActionDisabled}
                sx={{fontSize: 11, py: 0.25}}
            >
                {content.capabilitiesButtonLabel || 'List all AI capabilities'}
            </Button>
        </Stack>

        {/* Automatic capabilities */}
        {content.automaticCapabilities?.length > 0 && (
            <Stack spacing={1.5} pt={0.25}>
                {content.automaticCapabilities.map((cap) => {
                    const Icon = cap.icon;
                    return (
                        <Stack key={cap.title} direction="row" spacing={1} alignItems="flex-start">
                            <Icon sx={{fontSize: 14, color: 'text.secondary', mt: 0.1, flexShrink: 0}}/>
                            <Stack spacing={0.1}>
                                <Typography variant="caption" fontWeight={600} sx={{fontSize: 11}}>
                                    {cap.title}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" sx={{fontSize: 10}}>
                                    {cap.description}
                                </Typography>
                            </Stack>
                        </Stack>
                    );
                })}
            </Stack>
        )}
    </Stack>
);

const EmptyState = ({
    documentTitle,
    onAction,
    promptActionDisabled = false,
    preset = 'report',
}) => {
    const content = EMPTY_STATE_CONTENT[preset] || EMPTY_STATE_CONTENT.report;
    const meta = CONTEXT_META[preset] || CONTEXT_META.report;
    const isGlobal = preset === 'global';
    const isChart = preset === 'chart';

    return (
        <Stack py={1} px={0.5}>
            <Stack spacing={1.5}>
                <AssistantHeader
                    meta={meta}
                    isGlobal={isGlobal}
                    isChart={isChart}
                    documentTitle={documentTitle}
                    introText={content.intro}
                />
                {isGlobal
                    ? <GlobalEmptyState content={content} onAction={onAction} promptActionDisabled={promptActionDisabled}/>
                    : <ContextualEmptyState content={content} onAction={onAction} promptActionDisabled={promptActionDisabled}/>
                }
            </Stack>
        </Stack>
    );
};

export {EMPTY_STATE_CONTENT};

/** Compact Chart composer chips — same prompts as the Chart empty-state actions. */
export function chartPhraseActions(content = EMPTY_STATE_CONTENT.chart) {
    return (content?.manualWorkflows || []).flatMap((workflow) => workflow.actions || []);
}

export default EmptyState;
